
import { useState, useEffect, useRef } from "react";
import type { PendingSignature } from "@/types/signature";

export const usePendingSignatures = (loading: boolean, value: string) => {
  const [pendingSignature, setPendingSignature] = useState<PendingSignature | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState<number>(30);
  const intervalRef = useRef<NodeJS.Timeout>();
  const valueRef = useRef(value);

  // Обновляем значение в ref при изменении
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  // Функция очистки ресурсов
  const cleanup = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = undefined;
    }
    setPendingSignature(null);
    setRemainingSeconds(30);
  };

  // Очищаем интервал при размонтировании
  useEffect(() => {
    return cleanup;
  }, []);

  // Обрабатываем изменение состояния загрузки
  useEffect(() => {
    // Если загрузка завершена, очищаем состояние
    if (!loading) {
      console.log('[usePendingSignatures] Loading finished, cleaning up state');
      cleanup();
      return;
    }

    // Добавляем новую ожидающую подпись только если её ещё нет
    if (loading && value && !pendingSignature) {
      console.log('[usePendingSignatures] Adding new pending signature:', value);
      
      const id = Math.random().toString(36).substring(7);
      const newPendingSignature: PendingSignature = {
        id,
        text: value,
        startTime: Date.now()
      };
      
      setPendingSignature(newPendingSignature);
      setRemainingSeconds(30);

      // Запускаем обратный отсчет
      intervalRef.current = setInterval(() => {
        setRemainingSeconds(prev => {
          const newValue = prev - 1;
          if (newValue <= 0) {
            cleanup();
            return 30;
          }
          return newValue;
        });
      }, 1000);
    }
  }, [loading, value, pendingSignature]);

  const getPendingSignaturesDisplay = () => {
    if (!pendingSignature) return [];
    
    // Проверяем, не истекло ли время ожидания
    const elapsedTime = (Date.now() - pendingSignature.startTime) / 1000;
    if (elapsedTime >= 30) {
      cleanup();
      return [];
    }

    return [{
      text: pendingSignature.text,
      svg_content: '',
      remainingSeconds: remainingSeconds
    }];
  };

  return {
    pendingSignatures: getPendingSignaturesDisplay(),
    isGenerating: !!pendingSignature
  };
};
